import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { ReactComponent as ComputerSVG } from "../logo.svg";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Link from "@mui/material/Link";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const drawerWidth = 240;

const theme = createTheme({
  palette: {
    primary: {
      main: "#061966", // Navy Blue
    },
    secondary: {
      main: "#173FA3", // Royal Blue
    },
  },
  typography: {
    fontFamily: "'Noto Sans JP', sans-serif",
    button: {
      textTransform: "none",
      fontSize: "16px",
      fontWeight: 400,
    },
  },
  components: {
    MuiAppBar: {
      defaultProps: {
        color: "primary",
      },
      styleOverrides: {
        root: {
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: "none",
        color: "#DCE9FF", // Light Blue
      },
    },
    MuiListItemText: {
      defaultProps: {
        primaryTypographyProps: {
          fontSize: "16px",
          fontFamily: ["'Noto Sans JP', sans-serif"],
          color: "#DCE9FF", // Light Blue
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        sx: { marginRight: 2 },
        elevation: 0,
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        "aria-controls": "panel1a-content",
        sx: {
          fontSize: "16px",
          fontFamily: ["'Noto Sans JP', sans-serif"],
          color: "#DCE9FF", // Light Blue
        },
      },
    },
    MuiButton: {
      defaultProps: {
        sx: {
          color: "#8E9D5E", // Light Blue
          fontSize: "16px",
          padding: "15px",
          textTransform: "none",
          "&:hover": {
            color: "#8E9D5E", // Pink for hover state
          },
        },
      },
    },
  },
});

export const Navigation = () => {
  /**
   * Handles Mobile drawer
   */
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  /**
   * Handle drop down Services menu for desktop screens
   */
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const drawer = (
    <Box
      sx={{
        textAlign: "center",
        bgcolor: "#DCE9FF",
        height: "100%",
        padding: "20px 0",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mt: 2,
          mb: 3,
          fontFamily: "'Noto Sans JP', sans-serif",
          fontWeight: 400,
          color: "#173FA3",
        }}
      >
        <Link href="/">
          <ComputerSVG className="navbar-brand-div" />
        </Link>
      </Typography>
      <List sx={{ padding: "0 10px" }}>
        <ListItem key="Services" disablePadding>
          <ListItemButton
            sx={{ textAlign: "left", padding: "10px 16px" }}
            onClick={handleDrawerToggle}
          >
            <Link
              href="/services/"
              sx={{
                color: "#173FA3",
                fontWeight: 500,
                display: "block",
                width: "100%",
                textDecoration: "none",
                "& .MuiListItemText-primary": {
                  color: "#173FA3",
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: "18px",
                  fontWeight: 500,
                },
              }}
            >
              <ListItemText primary="Services" />
            </Link>
          </ListItemButton>
        </ListItem>
        <ListItem key="About Us" disablePadding>
          <ListItemButton
            sx={{ textAlign: "left", padding: "10px 16px" }}
            onClick={handleDrawerToggle}
          >
            <Link
              href="/about-us/"
              sx={{
                color: "#173FA3",
                fontWeight: 500,
                display: "block",
                width: "100%",
                textDecoration: "none",
                "& .MuiListItemText-primary": {
                  color: "#173FA3",
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: "18px",
                  fontWeight: 500,
                },
              }}
            >
              <ListItemText primary="About Us" />
            </Link>
          </ListItemButton>
        </ListItem>
        <ListItem key="Insights" disablePadding>
          <ListItemButton
            sx={{ textAlign: "left", padding: "10px 16px" }}
            onClick={handleDrawerToggle}
          >
            <Link
              onClick={(e) => {
                e.preventDefault();
                window.open("https://medium.com/@manateelabsllc", "_blank");
              }}
              sx={{
                color: "#173FA3",
                fontWeight: 500,
                display: "block",
                width: "100%",
                textDecoration: "none",
                "& .MuiListItemText-primary": {
                  color: "#173FA3",
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: "18px",
                  fontWeight: 500,
                },
              }}
            >
              <ListItemText primary="Insights" />
            </Link>
          </ListItemButton>
        </ListItem>
        <ListItem key="Contact" disablePadding>
          <ListItemButton
            sx={{ textAlign: "left", padding: "10px 16px" }}
            onClick={handleDrawerToggle}
          >
            <Link
              href="#contact"
              sx={{
                color: "#8E9D5E",
                fontWeight: 500,
                display: "block",
                width: "100%",
                textDecoration: "none",
                "& .MuiListItemText-primary": {
                  color: "#8E9D5E",
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: "18px",
                  fontWeight: 500,
                },
              }}
            >
              <ListItemText primary="Contact" />
            </Link>
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex", width: "100%" }}>
        <AppBar
          component="nav"
          sx={{
            bgcolor: "#DCE9FF",
            position: { xs: "fixed", sm: "sticky" },
            top: 0,
            zIndex: 1000,
            width: "100%",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Toolbar>
            <Typography
              variant="h6"
              component="div"
              sx={{
                flexGrow: 1,
                display: { xs: "block", sm: "block" },
                fontFamily: "'Noto Sans JP', sans-serif",
                fontWeight: 400,
              }}
            >
              <Link href="/">
                <ComputerSVG className="navbar-brand-div" />
              </Link>
            </Typography>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Button key="HomeButton" href="/" sx={{ color: "#061966" }}>
                Home
              </Button>
              <Button
                key="ServicesButton"
                href="/services/"
                sx={{ color: "#061966" }}
              >
                Services
              </Button>
              <Button
                key="AboutButton"
                href="/about-us/"
                sx={{ color: "#061966" }}
              >
                About Us
              </Button>
              <Button
                key="InsightsButton"
                onClick={() => {
                  window.open("https://medium.com/@manateelabsllc", "_blank");
                }}
                sx={{ color: "#061966" }}
              >
                Insights
              </Button>
              <Button
                key="ContactButton"
                href="#contact"
                sx={{
                  bgcolor: "#8E9D5E",
                  color: "#061966",
                  "&:hover": {
                    bgcolor: "#f7a9e0",
                  },
                  ml: 1,
                }}
              >
                Contact Us
              </Button>
            </Box>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: "none" }, color: "#8E9D5E" }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box component="nav">
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                bgcolor: "#061966",
              },
            }}
          >
            {drawer}
          </Drawer>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
